import React, { memo, useCallback } from 'react';
import {
  CPO_CEO_FILAS,
  getIndiceTotalForFila,
  patchIndiceConTotales,
} from '../../data/indicesCpoCeo';
import { cpoCeoTieneDatos } from '../../utils/mspReadHelpers';
import { preserveScrollWhile } from '../../utils/preserveScrollPosition';
import MspReadEmpty from './MspReadEmpty';

const IndiceInput = memo(function IndiceInput({ fieldKey, value, onChange, readOnly, ariaLabel, kind }) {
  const display = value !== '' && value != null ? String(value) : '';

  if (readOnly) {
    const filled = display !== '';
    return (
      <span
        className={`cpo-ceo-val-readonly${filled ? ' cpo-ceo-val-readonly--filled' : ' cpo-ceo-val-readonly--empty'} cpo-ceo-val-readonly--${kind}`}
      >
        {filled ? display : '—'}
      </span>
    );
  }

  return (
    <input
      id={fieldKey}
      name={fieldKey}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      className={`cpo-ceo-input cpo-ceo-input--${kind}`}
      value={display}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^\d]/g, '');
        preserveScrollWhile(e.target, () => onChange(fieldKey, raw));
      }}
      aria-label={ariaLabel}
      placeholder="0"
    />
  );
});

function CpoTotalValue({ value, rowLabel, rowTheme }) {
  const filled = value !== '' && value != null;
  return (
    <span
      className={`msp-total-val cpo-ceo-total cpo-ceo-total--${rowTheme}${filled ? ' msp-total-val--filled' : ' msp-total-val--empty'}`}
      title={filled ? `Total ${rowLabel} (C + P + O)` : 'Se calcula al ingresar C, P y O'}
      aria-live="polite"
    >
      {filled ? value : '—'}
    </span>
  );
}

/**
 * Tabla MSP «8 Índices CPO-ceo» — fila D (C,P,O) y fila d (c,e,o) con totales automáticos.
 * C, P y O son editables; TOTAL = suma automática (solo lectura).
 */
function IndicesCpoCeoTable({
  form = {},
  onChange,
  readOnly = false,
  showSectionTitle = true,
}) {
  const setField = useCallback(
    (key, raw) => {
      if (!onChange) return;
      onChange((prev) => patchIndiceConTotales(prev, key, raw));
    },
    [onChange],
  );

  const tieneDatosCpo = cpoCeoTieneDatos(form);

  if (readOnly && !tieneDatosCpo) {
    return (
      <MspReadEmpty
        icon="bi-calculator"
        message="Índices CPO-ceo sin registrar. Ingréselos en la ficha odontológica."
      />
    );
  }

  return (
    <div className={`cpo-ceo-panel${readOnly ? ' cpo-ceo-panel--readonly' : ''}`}>
      {showSectionTitle && (
        <div className="cpo-ceo-panel__header">
          <i className="bi bi-calculator-fill" aria-hidden />
          8 Índices CPO-ceo
        </div>
      )}

      {!readOnly && (
        <div className="cpo-ceo-help" role="note">
          <i className="bi bi-info-circle" aria-hidden />
          <p>
            Ingrese <strong>C</strong>, <strong>P</strong> y <strong>O</strong> por separado en cada fila.
            La columna <strong>TOTAL</strong> (naranja) suma automáticamente C + P + O (o c + e + o).
          </p>
        </div>
      )}

      <div className="cpo-ceo-table-wrap">
        <table className="cpo-ceo-table" aria-label="Índices CPO y ceo">
          <colgroup>
            <col className="cpo-ceo-col--row" />
            <col className="cpo-ceo-col--val" />
            <col className="cpo-ceo-col--val" />
            <col className="cpo-ceo-col--val" />
            <col className="cpo-ceo-col--total" />
          </colgroup>
          <tbody>
            {CPO_CEO_FILAS.map((fila, blockIdx) => {
              const total = getIndiceTotalForFila(form, fila);
              return (
                <React.Fragment key={fila.rowLabel}>
                  {blockIdx > 0 ? (
                    <tr className="cpo-ceo-spacer" aria-hidden>
                      <td colSpan={5} />
                    </tr>
                  ) : null}
                  <tr className={`cpo-ceo-row-head cpo-ceo-block--${fila.rowTheme}`}>
                    <th rowSpan={2} className="cpo-ceo-row-label" title={fila.rowHint} scope="row">
                      <div className="cpo-ceo-row-label__inner">
                        <span className="cpo-ceo-row-label__letter">{fila.rowLabel}</span>
                        <span className="cpo-ceo-row-label__hint">
                          {fila.rowTheme === 'permanente' ? 'Perm.' : 'Dec.'}
                        </span>
                      </div>
                    </th>
                    {fila.cols.map((col) => (
                      <th
                        key={`${fila.rowLabel}-head-${col.key}`}
                        className={`cpo-ceo-col-head cpo-ceo-col-head--${col.kind}`}
                        title={col.title}
                        scope="col"
                      >
                        <span className="cpo-ceo-col-head__letter">{col.label}</span>
                        <span className="cpo-ceo-col-head__sub">{col.title}</span>
                      </th>
                    ))}
                    <th className="cpo-ceo-col-head cpo-ceo-col-head--total" scope="col">
                      <span className="cpo-ceo-col-head__letter">Total</span>
                      <span className="cpo-ceo-col-head__sub">Suma</span>
                    </th>
                  </tr>
                  <tr className={`cpo-ceo-row-data cpo-ceo-block--${fila.rowTheme}`}>
                    {fila.cols.map((col) => (
                      <td
                        key={`${fila.rowLabel}-cell-${col.key}`}
                        className={`cpo-ceo-cell cpo-ceo-cell--input cpo-ceo-cell--${col.kind}`}
                      >
                        <IndiceInput
                          fieldKey={col.key}
                          value={form[col.key]}
                          readOnly={readOnly}
                          kind={col.kind}
                          ariaLabel={`${fila.rowLabel} — ${col.title} (${col.label})`}
                          onChange={setField}
                        />
                      </td>
                    ))}
                    <td className={`cpo-ceo-cell cpo-ceo-cell--total cpo-ceo-cell--total-${fila.rowTheme}`}>
                      <CpoTotalValue value={total} rowLabel={fila.rowLabel} rowTheme={fila.rowTheme} />
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <p className="cpo-ceo-leyenda small text-muted mb-0">
          C/c = caries · P/e = perdidos/extraídos · O/o = obturados · TOTAL = suma automática de la fila
        </p>
      )}
    </div>
  );
}

export default memo(IndicesCpoCeoTable);
